import { CSSProperties, FC, useMemo } from "react";
import { Chart } from "../../../components/charts/Chart";
import { LineChart } from "../../../components/charts/LineChart";
import { getTeamColors } from "components/colors";
import { BasketballProps } from "..";
import { extractFirstNonZero, ensurePair } from "../utils";
import QuarterBox from "./components/QuarterBox";

export const Stats: FC<BasketballProps> = ({ language, status }) => {
  const { home, away } = getTeamColors(status as any);

  const data = useMemo(() => {
    if (!status || typeof status !== "object") return {} as any;

    const splitStatus = status.sts?.split("|");
    if (!splitStatus) return {} as any;

    // Core scoring breakdowns
    const threePts = extractFirstNonZero(splitStatus, ["3 Pts", "3PTS", "3PT"]);
    const twoPts = extractFirstNonZero(splitStatus, ["2 Pts", "2PTS", "2PT"]);
    const fourPts = extractFirstNonZero(splitStatus, ["4 Pts", "4PTS", "4PT"]);
    const onePts = extractFirstNonZero(splitStatus, ["1 Pts", "1PTS", "1PT"]);

    // Free throws (some feeds use Ft, others Free Throws)
    const freeThrows = extractFirstNonZero(splitStatus, [
      "Ft",
      "FT",
      "Free Throws",
      "FREE THROWS",
    ]);

    // Treat FREE THROWS as already % conversion (0-100 range) — do not normalize
    const freeThrowsPct: [number, number] = [
      Number(freeThrows?.[0] ?? 0),
      Number(freeThrows?.[1] ?? 0),
    ];

    // Disciplinary / misc
    const fouls = extractFirstNonZero(splitStatus, ["Fouls", "FOULS", "Foul"]);
    const timeouts = extractFirstNonZero(splitStatus, [
      "Time Outs#T.O",
      "Time Outs",
      "Timeouts",
      "T.O",
    ]);
    const bonus = extractFirstNonZero(splitStatus, ["BONUS"]);

    // Rebounds – feeds vary; RD seen in sample, also support RB/REB
    const rebounds = extractFirstNonZero(splitStatus, [
      "RD",
      "REB",
      "Rebounds",
      "RB",
    ]);

    // Quarter / half / total scores (from status.c)
    const quarters: { key: string; v: [number, number] }[] = [];
    const c = (status as any).c || {};

    // Always include Q1..Q4
    (["Q1", "Q2", "Q3", "Q4"] as const).forEach((k) => {
      quarters.push({ key: k, v: ensurePair(c?.[k]) });
    });

    // Always include H1, T
    (["H1", "T"] as const).forEach((k) => {
      quarters.push({ key: k, v: ensurePair(c?.[k]) });
    });

    // Optionally include overtimes if present
    (["OT1", "OT2", "OT3", "OT4"] as const).forEach((k) => {
      const v = c?.[k];
      if (Array.isArray(v) && v.length === 2) {
        quarters.push({ key: k, v: [Number(v[0]) || 0, Number(v[1]) || 0] });
      }
    });

    return {
      threePts,
      twoPts,
      fourPts,
      onePts,
      freeThrows: freeThrowsPct,
      fouls,
      timeouts,
      bonus,
      rebounds,
      quarters,
    } as const;
  }, [status]);

  const cssVariables = {
    ["--home-color" as "--home-color"]: home,
    ["--away-color" as "--away-color"]: away,
  } as CSSProperties;
  const gridColumns = "repeat(auto-fit, minmax(14vw, 1fr))";

  return (
    <div
      className="flex w-full flex-col items-center justify-start gap-[2vh]"
      style={cssVariables}
    >
      {/* SCORE BREAKDOWN */}
      <div className="flex w-full flex-row items-center justify-between gap-[2vw]">
        <Chart
          homeValue={data.threePts ? data.threePts[0] : 0}
          awayValue={data.threePts ? data.threePts[1] : 0}
          title="3 POINTS"
        />
        <Chart
          homeValue={data.twoPts ? data.twoPts[0] : 0}
          awayValue={data.twoPts ? data.twoPts[1] : 0}
          title="2 POINTS"
        />

        <Chart
          homeValue={data.freeThrows[0]}
          awayValue={data.freeThrows[1]}
          title="FREE THROWS"
        />

        {data.fourPts && (data.fourPts[0] !== 0 || data.fourPts[1] !== 0) && (
          <Chart
            homeValue={data.fourPts[0]}
            awayValue={data.fourPts[1]}
            title="4 POINTS"
          />
        )}
        {data.onePts && (data.onePts[0] !== 0 || data.onePts[1] !== 0) && (
          <Chart
            homeValue={data.onePts[0]}
            awayValue={data.onePts[1]}
            title="1 POINT"
          />
        )}
      </div>

      {/* QUARTERS Q1..Q4 */}
      <div
        className="grid w-full gap-[1.5vw]"
        style={{ gridTemplateColumns: gridColumns }}
      >
        {data.quarters
          .filter(({ key }: { key: string }) =>
            ["Q1", "Q2", "Q3", "Q4"].includes(key)
          )
          .map(({ key, v }: { key: string; v: [number, number] }) => (
            <QuarterBox key={key} title={key} home={v[0]} away={v[1]} />
          ))}
      </div>

      {/* HALVES & TOTAL */}
      <div
        className="grid w-full gap-[1.5vw]"
        style={{ gridTemplateColumns: gridColumns }}
      >
        {data.quarters
          .filter(({ key }: { key: string }) => ["H1", "T"].includes(key))
          .map(({ key, v }: { key: string; v: [number, number] }) => (
            <QuarterBox key={key} title={key} home={v[0]} away={v[1]} />
          ))}
      </div>

      {/* LINE CHARTS STACKED */}
      <div className="flex w-full flex-col gap-[2vw]">
        {data.fouls && (data.fouls[0] !== 0 || data.fouls[1] !== 0) && (
          <LineChart
            homeValue={data.fouls[0]}
            awayValue={data.fouls[1]}
            title="FOULS"
          />
        )}
        {data.timeouts &&
          (data.timeouts[0] !== 0 || data.timeouts[1] !== 0) && (
            <LineChart
              homeValue={data.timeouts[0]}
              awayValue={data.timeouts[1]}
              title="TIME-OUTS"
            />
          )}
        {data.bonus && (data.bonus[0] !== 0 || data.bonus[1] !== 0) && (
          <LineChart
            homeValue={data.bonus[0]}
            awayValue={data.bonus[1]}
            title="BONUS"
          />
        )}
        {data.rebounds &&
          (data.rebounds[0] !== 0 || data.rebounds[1] !== 0) && (
            <LineChart
              homeValue={data.rebounds[0]}
              awayValue={data.rebounds[1]}
              title="REBOUNDS"
            />
          )}
      </div>

      {/* OVERTIME */}
      {data.quarters.some(({ key }: { key: string }) =>
        key.startsWith("OT")
      ) && (
        <div
          className="grid w-full gap-[1.5vw]"
          style={{ gridTemplateColumns: gridColumns }}
        >
          {data.quarters
            .filter(({ key }: { key: string }) => key.startsWith("OT"))
            .map(({ key, v }: { key: string; v: [number, number] }) => (
              <QuarterBox key={key} title={key} home={v[0]} away={v[1]} />
            ))}
        </div>
      )}
    </div>
  );
};
